import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Building2, ArrowLeft, Settings, Users, Trash2 } from 'lucide-react';
import { PageHeader } from '../../components/shared/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { LoadingState } from '../../components/shared/LoadingState';
import { ErrorState } from '../../components/shared/ErrorState';
import { ConfirmDialog } from '../../components/shared/ConfirmDialog';
import { useOrganizations } from '../../core/hooks/useOrganizations';
import { useAuth } from '../../core/contexts/AuthContext';
import { UserRole } from '../../core/models/User';
import { OrganizationDialog } from './components/OrganizationDialog';
import { ManageClientsDialog } from './components/ManageClientsDialog';
import { useClients } from '../../core/hooks/useClients';
import { VoiceAllowance } from './components/VoiceAllowance';

export const OrganizationDetailPage: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const { organizations, loading, error, updateOrganization, deleteOrganization } = useOrganizations();
  const { currentUser } = useAuth();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isManageClientsOpen, setIsManageClientsOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
  const effectiveOrgIdForClients = isSuperAdmin ? null : organizationId || null;
  // For SUPER_ADMIN, passing null lets useClients fetch all clients so they can assign any client to this org
  const { clients: allClients } = useClients(effectiveOrgIdForClients);

  if (loading) {
    return <LoadingState message="Loading client..." />;
  }

  if (error || !organizationId) {
    return <ErrorState message="Failed to load client" />;
  }

  const organization = organizations.find((org) => org.id === organizationId);

  if (!organization) {
    return <ErrorState message="Client not found" />;
  }

  const assignedClients = allClients.filter((client) => organization.clientIds?.includes(client.id));

  const handleUpdateOrganization = async (data: any) => {
    try {
      await updateOrganization(organization.id, data);
      setIsEditDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to update organization:', err);
      const errorMessage = err?.message || 'Failed to update client. Please try again.';
      alert(errorMessage);
    }
  };

  const handleUpdateClients = async (clientIds: string[]) => {
    try {
      await updateOrganization(organization.id, { clientIds });
    } catch (err: any) {
      console.error('Failed to update organization clients:', err);
      const errorMessage = err?.message || 'Failed to update client projects. Please try again.';
      alert(errorMessage);
    }
  };

  const handleDeleteOrganization = async () => {
    try {
      setDeleting(true);
      await deleteOrganization(organization.id);
      setIsDeleteDialogOpen(false);
      navigate('/organizations');
    } catch (err: any) {
      console.error('Failed to delete organization:', err);
      const errorMessage = err?.message || 'Failed to delete client. Please try again.';
      alert(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={organization.name}
        description="Client details"
        icon={Building2}
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {isSuperAdmin && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditDialogOpen(true)}
                >
                  <Settings className="h-4 w-4" />
                  Edit Client
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsManageClientsOpen(true)}
                >
                  <Users className="h-4 w-4" />
                  Manage Projects
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => setIsDeleteDialogOpen(true)}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Client
                </Button>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate('/organizations')}>
              <ArrowLeft className="h-4 w-4" />
              Back to Clients
            </Button>
          </div>
        }
      />

      <Card>
        <CardContent className="pt-6 space-y-6">
          {/* Overview */}
          <section>
            <h3 className="text-sm font-medium text-muted-foreground mb-2">Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="font-medium capitalize">{organization.status}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Projects</p>
                <p className="font-medium">{organization.clientIds?.length || 0} projects</p>
              </div>
            </div>
          </section>

          {/* Assigned Projects */}
          <section>
            <h3 className="text-sm font-medium text-muted-foreground mb-2">Assigned Projects</h3>
            {assignedClients.length > 0 ? (
              <div className="space-y-1">
                {assignedClients.map((client) => (
                  <div key={client.id} className="text-sm">
                    <Link
                      to={`/clients/${client.id}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {client.name}
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No projects assigned. Use Manage Projects to add.
              </p>
            )}
          </section>

          {/* Address */}
          {organization.address && (
            <section>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Address</h3>
              <p className="font-medium">
                {organization.address.street}
                <br />
                {organization.address.city}, {organization.address.state}{' '}
                {organization.address.postalCode}
                <br />
                {organization.address.country}
              </p>
            </section>
          )}
        </CardContent>
      </Card>

      {isSuperAdmin && <VoiceAllowance clientId={organization.id} />}

      {isSuperAdmin && (
        <>
          <OrganizationDialog
            open={isEditDialogOpen}
            onClose={() => setIsEditDialogOpen(false)}
            organization={organization}
            onSubmit={handleUpdateOrganization}
          />
          <ManageClientsDialog
            open={isManageClientsOpen}
            onClose={() => setIsManageClientsOpen(false)}
            organizationId={organization.id}
            currentClientIds={organization.clientIds || []}
            onUpdate={handleUpdateClients}
          />
          <ConfirmDialog
            open={isDeleteDialogOpen}
            onClose={() => {
              if (!deleting) setIsDeleteDialogOpen(false);
            }}
            onConfirm={handleDeleteOrganization}
            title="Delete client"
            description={`Are you sure you want to permanently delete "${organization.name}"? This may affect associated projects and users.`}
            confirmLabel="Delete"
            cancelLabel="Cancel"
            variant="destructive"
            isLoading={deleting}
          />
        </>
      )}
    </div>
  );
};
